import React, { useEffect, useState } from "react";
import { supabase } from "../SupabaseClient.tsx";
import ActionButton from "../components/ActionButton.tsx";
import { PiArrowRight, PiCheckCircle } from "react-icons/pi";
import "./Reimburse.css";

const Reimburse = () => {
  const [totalAmount, setTotalAmount] = useState<number>(0);
  const [reimbursementAmount, setReimbursementAmount] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [interestRate, setInterestRate] = useState<number>(0);
  const [retirementDate, setRetirementDate] = useState<string>("");
  const [showInstructions, setShowInstructions] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [eligibleOrders, setEligibleOrders] = useState<any[]>([]);

  // Calculate the future value of money using compound interest
  const calculateFutureValue = (principal: number): number => {
    if (!retirementDate || interestRate === 0) return 0;

    const currentYear = new Date().getFullYear();
    const retirementYear = new Date(retirementDate).getFullYear();
    const years = retirementYear - currentYear;

    if (years <= 0) return 0;

    // Compound interest formula: FV = P(1 + r)^t
    const futureValue = principal * Math.pow(1 + interestRate / 100, years);
    return futureValue - principal; // Return just the growth amount
  };

  useEffect(() => {
    // Fetch user settings
    const fetchUserSettings = async () => {
      const { data, error } = await supabase
        .from("users")
        .select("interest_rate, retirement_date")
        .single();

      if (error) {
        console.error("Error fetching user settings:", error);
      } else if (data) {
        setInterestRate(data.interest_rate || 0);
        setRetirementDate(data.retirement_date || "");
      }
    };

    // Fetch eligible orders from Supabase
    const fetchEligibleOrders = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("view_reimburse_amount")
        .select("id, amount, purchase_date, status_id, reimbursable_amount")
        .gt("reimbursable_amount", 0)
        .order("purchase_date", { ascending: true });

      if (error) {
        console.error("Error fetching eligible orders:", error);
      } else {
        // Calculate total eligible amount
        const total = (data || []).reduce(
          (sum, order) => sum + (order.reimbursable_amount || 0),
          0
        );
        setTotalAmount(total);
        setEligibleOrders(data || []);
      }
      setLoading(false);
    };

    Promise.all([fetchUserSettings(), fetchEligibleOrders()]);
  }, []);

  const handleReimbursementChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;

    // Validate input to ensure it's a valid number and not greater than total
    if (
      value === "" ||
      (!isNaN(parseFloat(value)) && parseFloat(value) <= totalAmount)
    ) {
      setReimbursementAmount(value);
    }
  };

  const handleContinue = () => {
    // Show the instructions when continue is clicked
    setShowInstructions(true);
    console.log(`Reimbursing $${reimbursementAmount}`);
  };

  const handleCompleteReimbursement = async () => {
    if (!reimbursementAmount || parseFloat(reimbursementAmount) <= 0) return;

    setProcessing(true);
    setErrorMessage("");
    setSuccessMessage("");

    try {
      const reimbursementValue = parseFloat(reimbursementAmount);
      let remainingAmount = reimbursementValue;
      const today = new Date().toISOString().split("T")[0]; // YYYY-MM-DD format
      const ordersToUpdate: { id: number; status_id: number }[] = [];
      const reimbursementRecords: {
        order_id: number;
        reimburse_date: string;
        reimburse_amount: number;
      }[] = [];

      // Distribute the reimbursement amount across eligible orders
      for (const order of eligibleOrders) {
        if (remainingAmount <= 0) break;

        // Calculate how much we can reimburse from this order
        const availableAmount = order.reimbursable_amount;
        const amountToReimburse = Math.min(availableAmount, remainingAmount);

        if (amountToReimburse > 0) {
          // Create a reimbursement record
          reimbursementRecords.push({
            order_id: order.id,
            reimburse_date: today,
            reimburse_amount: Number(amountToReimburse.toFixed(2)),
          });

          // Determine new status (3 = partially reimbursed, 4 = fully reimbursed)
          const newRemainingAmount = availableAmount - amountToReimburse;
          const newStatus = newRemainingAmount <= 0 ? 4 : 3;

          // Add to orders that need status update
          ordersToUpdate.push({
            id: order.id,
            status_id: newStatus,
          });

          // Reduce the remaining amount to reimburse
          remainingAmount -= amountToReimburse;
        }
      }

      // Insert reimbursement records
      const { error: insertError } = await supabase
        .from("order_reimburse")
        .insert(reimbursementRecords);

      if (insertError) throw insertError;

      // Update order statuses
      for (const order of ordersToUpdate) {
        const { error: updateError } = await supabase
          .from("orders")
          .update({ status_id: order.status_id })
          .eq("id", order.id);

        if (updateError) throw updateError;
      }

      setSuccessMessage(
        `Reimbursement of $${reimbursementValue.toFixed(
          2
        )} processed successfully!`
      );

      // Refresh eligible orders after successful reimbursement
      const { data: refreshedData } = await supabase
        .from("view_reimburse_amount")
        .select("id, amount, purchase_date, status_id, reimbursable_amount")
        .gt("reimbursable_amount", 0)
        .order("purchase_date", { ascending: true });

      if (refreshedData) {
        const total = refreshedData.reduce(
          (sum, order) => sum + (order.reimbursable_amount || 0),
          0
        );
        setTotalAmount(total);
        setEligibleOrders(refreshedData);
      }

      // Reset form
      setReimbursementAmount("");
      setShowInstructions(false);
    } catch (error) {
      console.error("Error processing reimbursement:", error);
      setErrorMessage("Failed to process reimbursement. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  // Calculate value lost
  const valueLost = calculateFutureValue(
    parseFloat(reimbursementAmount || "0")
  );

  return (
    <div className="global-page-screen">
      <div className="page">
        <div className="page-header">
          <div className="page-header-title">
            <h1>Reimburse</h1>
            <p>
              Reimburse for any of your eligible expenses. Choose how much you
              want to reimburse and Shoebox will take care of the rest.
            </p>
          </div>
        </div>
        <div className="reimburse-container">
          <div className="savings-summary">
            <div className="savings-total">
              <h2 className="container-label-header">Total Available for Reimbursement</h2>
              <h1>
                ${totalAmount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              </h1>
              <p>Total from all eligible receipts</p>
            </div>
            <div className="savings-info">
              <p>
                This represents the total amount of eligible expenses that are
                available for tax-free reimbursement from your HSA.
              </p>
            </div>
          </div>

          {!showInstructions ? (
            <div className="reimburse-form-container">
              <h2 className="container-label-header">How much would you like to reimburse?</h2>

              <div className="reimburse-form">
                <div className="reimburse-field">
                  <label htmlFor="reimburse-amount">Amount to Reimburse</label>
                  <div className="input-with-prefix">
                    <span className="input-prefix">$</span>
                    <input
                      type="number"
                      id="reimburse-amount"
                      value={reimbursementAmount}
                      onChange={handleReimbursementChange}
                      placeholder="Enter amount"
                      step="0.01"
                      min="0"
                      max={totalAmount}
                      disabled={loading}
                    />
                  </div>
                  <div className="reimburse-remaining">
                    <p>
                      Remaining after reimbursement:
                      <span className="reimburse-remaining-amount">
                        $
                        {(totalAmount - parseFloat(reimbursementAmount || "0"))
                          .toFixed(2)
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </span>
                    </p>
                  </div>
                  <div className="reimburse-remaining">
                    <p>
                      Lifetime value lost by reimbursing today:
                      <span className="reimburse-remaining-amount">
                        $
                        {valueLost
                          .toFixed(2)
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="reimburse-action">
                  <ActionButton
                    icon={<PiArrowRight />}
                    text="Continue"
                    onClick={handleContinue}
                    disabled={
                      loading ||
                      !reimbursementAmount ||
                      parseFloat(reimbursementAmount) <= 0
                    }
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="reimburse-instructions-container">
              <h3>How to complete your reimbursement</h3>

              <div className="reimburse-instructions">
                <ol className="instruction-steps">
                  <li>
                    <div className="instruction-step">
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <h4>Log in to your HSA provider's website</h4>
                        <p>
                          Access your Health Savings Account through your
                          provider's portal.
                        </p>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="instruction-step">
                      <div className="step-number">2</div>
                      <div className="step-content">
                        <h4>Navigate to the reimbursement section</h4>
                        <p>
                          Look for "Reimbursement", "Distributions", or
                          "Withdrawals" in the menu.
                        </p>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="instruction-step">
                      <div className="step-number">3</div>
                      <div className="step-content">
                        <h4>Enter reimbursement amount</h4>
                        <p>
                          Enter exactly $
                          {parseFloat(reimbursementAmount).toFixed(2)} as your
                          reimbursement amount.
                        </p>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="instruction-step">
                      <div className="step-number">4</div>
                      <div className="step-content">
                        <h4>Complete the reimbursement process</h4>
                        <p>
                          Follow your HSA provider's instructions to complete
                          the withdrawal to your bank account.
                        </p>
                      </div>
                    </div>
                  </li>
                  <li>
                    <div className="instruction-step">
                      <div className="step-number">5</div>
                      <div className="step-content">
                        <h4>Shoebox will mark receipts as reimbursed</h4>
                        <p>
                          Once you confirm below, Shoebox will update your
                          eligible receipts to show they've been reimbursed.
                        </p>
                      </div>
                    </div>
                  </li>
                </ol>

                <div className="reimburse-action-group">
                  <ActionButton
                    secondary={true}
                    text="Cancel"
                    onClick={() => setShowInstructions(false)}
                    disabled={processing}
                  />
                  <ActionButton
                    icon={<PiCheckCircle />}
                    text={processing ? "Processing..." : "Finalize"}
                    onClick={handleCompleteReimbursement}
                    disabled={processing}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {successMessage && (
          <div className="success-message">
            <PiCheckCircle /> {successMessage}
          </div>
        )}
        {errorMessage && <div className="error-message">{errorMessage}</div>}
      </div>
    </div>
  );
};

export default Reimburse;
